import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import './QuizResults.css';

const QuizResults: React.FC = () => {
  const navigate = useNavigate();
  const { id, chapterId, quizId } = useParams();
  const location = useLocation();
  const { answers, quiz } = location.state || {};

  // Mock results calculation
  const calculateResults = () => {
    let correct = 0;
    let totalScore = 0;
    let maxScore = 0;
    const questionResults: any[] = [];

    quiz?.sections.forEach((section: any) => {
      section.questions.forEach((question: any) => {
        maxScore += question.score;
        const userAnswer = answers[question.id];
        let isCorrect = false;
        let earnedScore = 0;

        if (question.type === 'multiple_choice') {
          isCorrect = question.correctAnswer.includes(userAnswer);
        } else if (question.type === 'true_false') {
          isCorrect = question.correctAnswer === userAnswer;
        } else if (question.type === 'fill_blank') {
          isCorrect = question.correctAnswer.toLowerCase() === (userAnswer || '').toLowerCase();
        }

        if (isCorrect) {
          correct++;
          earnedScore = question.score;
          totalScore += question.score;
        }

        questionResults.push({
          ...question,
          userAnswer,
          isCorrect,
          earnedScore
        });
      });
    });

    const percentage = Math.round((totalScore / maxScore) * 100);
    const passed = percentage >= 60;

    return { correct, totalScore, maxScore, percentage, passed, questionResults };
  };

  const results = calculateResults();

  return (
    <div className="quiz-results-page">
      <header className="quiz-results-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1>Quiz Results</h1>
        <div className="header-spacer"></div>
      </header>

      <div className="quiz-results-content">
        {/* Results Summary */}
        <div className={`results-summary-card ${results.passed ? 'passed' : 'failed'}`}>
          <div className="result-icon-large">
            {results.passed ? '🎉' : '📝'}
          </div>
          <h2 className="result-title">
            {results.passed ? 'Congratulations!' : 'Keep Learning!'}
          </h2>
          <div className="score-display">
            <span className="score-current">{results.totalScore}</span>
            <span className="score-separator">/</span>
            <span className="score-total">{results.maxScore}</span>
          </div>
          <div className="percentage-display">
            <div className="percentage-circle">
              <span className="percentage-text">{results.percentage}%</span>
            </div>
          </div>
          <p className="result-message">
            You answered {results.correct} out of {results.questionResults.length} questions correctly
          </p>
          {results.passed ? (
            <span className="pass-badge">PASSED</span>
          ) : (
            <span className="fail-badge">NEED IMPROVEMENT</span>
          )}
        </div>

        {/* Detailed Results */}
        <div className="detailed-results-section">
          <h3 className="section-title">Review Your Answers</h3>
          {results.questionResults.map((result, index) => (
            <div
              key={result.id}
              className={`question-result-item ${result.isCorrect ? 'correct' : 'incorrect'}`}
            >
              <div className="question-result-header">
                <div className="question-result-number">
                  Q{index + 1}
                  {result.isCorrect ? (
                    <span className="result-icon">✓</span>
                  ) : (
                    <span className="result-icon">✗</span>
                  )}
                </div>
                <span className="question-result-score">
                  {result.earnedScore}/{result.score} pts
                </span>
              </div>
              <p className="question-result-text">{result.question}</p>
              
              <div className="answer-comparison">
                <div className="answer-item">
                  <span className="answer-label">Your Answer:</span>
                  <span className={`answer-value ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                    {result.type === 'multiple_choice' 
                      ? result.options[result.userAnswer]
                      : result.type === 'true_false'
                      ? (result.userAnswer ? 'True' : 'False')
                      : result.userAnswer || 'No answer'}
                  </span>
                </div>
                {!result.isCorrect && (
                  <div className="answer-item">
                    <span className="answer-label">Correct Answer:</span>
                    <span className="answer-value correct">
                      {result.type === 'multiple_choice'
                        ? result.options[result.correctAnswer[0]]
                        : result.type === 'true_false'
                        ? (result.correctAnswer ? 'True' : 'False')
                        : result.correctAnswer}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="results-actions">
          <button
            className="action-button retake-button"
            onClick={() => navigate(`/course/${id}/chapter/${chapterId}/quiz/${quizId}`)}
          >
            Retake Quiz
          </button>
          <button
            className="action-button continue-button"
            onClick={() => navigate(`/course/${id}/chapters`)}
          >
            Continue Learning
          </button>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default QuizResults;
